import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Plus, Search, Eye, ArrowRight, Check,
  Trash2, X, GripVertical, Send,
  Wrench, Package
} from 'lucide-react';
import ProductPickerModal from '../components/ProductPickerModal';
import { useWorkflowStore } from '../store/workflowStore';
import { useCatalogStore } from '../store/catalogStore';
import { toast } from '../components/Toast';
import type { WorkflowQuotation, QuotationStatus, LineItem } from '../types/workflow';
import { useAdminStore } from '../store/adminStore';

const fmtCurrency = (n: number) =>
  `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-LK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const uid = () => `li-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const STATUS_COLORS: Record<QuotationStatus, string> = {
  draft:     'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  approved:  'bg-emerald-100 text-emerald-700',
  rejected:  'bg-red-100 text-red-700',
  converted: 'bg-teal-100 text-teal-700',
  expired:   'bg-[#E8F2E3] text-[#4F7040]',
};

const EMPTY_LINE: Omit<LineItem, 'id' | 'lineTotal'> = {
  itemType: 'custom', name: '', description: '', modelNumber: '', warrantyPeriod: '',
  quantity: 1, unit: 'Pcs', unitPrice: 0, discountType: 'fixed', discountValue: 0,
};

function calcLine(l: Omit<LineItem, 'lineTotal' | 'id'>): number {
  const base = l.quantity * l.unitPrice;
  if (!l.discountValue) return base;
  const disc = l.discountType === 'percentage' ? base * (l.discountValue / 100) : l.discountValue;
  return Math.max(0, base - disc);
}

function LineItemRow({
  item, onChange, onDelete, onOpenPicker
}: {
  item: LineItem;
  onChange: (id: string, patch: Partial<LineItem>) => void;
  onDelete: (id: string) => void;
  onOpenPicker: (lineItemId: string) => void;
}) {
  const { catalogProducts, brands, categories } = useCatalogStore();
  const [query, setQuery] = useState(item.name);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLTableCellElement>(null);

  // Sync query when item.name changes externally (e.g. after picker selection)
  useEffect(() => { setQuery(item.name); }, [item.name]);

  const results = useMemo(() => {
    if (!query.trim() || query.trim().length < 2) return [];
    const q = query.toLowerCase();
    return catalogProducts
      .filter(p => p.status === 'active' && (
        p.name.toLowerCase().includes(q) ||
        (p.modelNumber || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        brands.find(b => b.id === p.brandId)?.name.toLowerCase().includes(q) ||
        categories.find(c => c.id === p.categoryId)?.name.toLowerCase().includes(q) ||
        (p.resolution || '').toLowerCase().includes(q) ||
        (p.productType || '').toLowerCase().includes(q) ||
        (p.shortDescription || '').toLowerCase().includes(q)
      ))
      .slice(0, 8);
  }, [query, catalogProducts, brands, categories]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (product: typeof catalogProducts[0]) => {
    const brand = brands.find(b => b.id === product.brandId);
    const category = categories.find(c => c.id === product.categoryId);
    const price = product.salePrice ?? product.regularPrice;
    onChange(item.id, {
      itemType: 'product',
      productId: product.id,
      name: product.name,
      description: product.shortDescription || `${brand?.name || ''} ${product.productType || ''} ${product.modelNumber || ''}`.trim(),
      modelNumber: product.modelNumber || '',
      warrantyPeriod: product.warrantyPeriod || '24 Months',
      quantity: 1,
      unit: 'Pcs',
      unitPrice: price,
      costPrice: product.costPrice ?? 0,
    });
    setQuery(product.name);
    setOpen(false);
  };

  return (
    <tr className="border-b border-gray-100 group">
      {/* Drag handle placeholder */}
      <td className="px-2 py-2 text-gray-300 w-6">
        <GripVertical className="w-4 h-4" />
      </td>
      {/* Name + Autocomplete */}
      <td className="px-2 py-2 min-w-[220px] relative" ref={wrapRef}>
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
          placeholder="Type to search products…"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            onChange(item.id, { name: e.target.value });
            setOpen(true);
          }}
          onFocus={() => { if (query.trim().length >= 2) setOpen(true); }}
          autoComplete="off"
        />
        {/* Autocomplete Dropdown */}
        {open && results.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
            {results.map(p => {
              const brand = brands.find(b => b.id === p.brandId);
              const availStock = p.stock - p.reservedStock;
              return (
                <button
                  key={p.id}
                  type="button"
                  onMouseDown={() => handleSelect(p)}
                  className="w-full text-left px-3 py-2.5 hover:bg-[#7FA86A]/5 border-b border-gray-50 last:border-0 flex items-start gap-3 group/item transition-colors"
                >
                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {p.mainImage ? (
                      <img src={p.mainImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-5 h-5 text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-gray-900 truncate group-item-hover:text-[#7FA86A]">{p.name}</div>
                    <div className="text-[11px] text-gray-400">{brand?.name} · {p.modelNumber}</div>
                    {p.shortDescription && (
                      <div className="text-[11px] text-gray-400 truncate">{p.shortDescription}</div>
                    )}
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className={`text-[10px] font-medium ${availStock > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        Stock: {availStock}
                      </span>
                      {p.warrantyPeriod && (
                        <span className="text-[10px] text-purple-500">🛡 {p.warrantyPeriod}</span>
                      )}
                      <span className="text-[11px] font-bold text-[#7FA86A] ml-auto">
                        Rs. {(p.salePrice ?? p.regularPrice).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex gap-1 mt-1">
          <button
            type="button"
            onClick={() => onOpenPicker(item.id)}
            className="text-xs px-2 py-0.5 bg-[#7FA86A]/10 text-[#7FA86A] rounded hover:bg-[#7FA86A]/20 font-medium transition-colors"
          >
            + Pick item
          </button>
          <button
            type="button"
            onClick={() => onChange(item.id, { itemType: 'custom' })}
            className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded hover:bg-gray-200 transition-colors"
            title="Manual / Custom item"
          >
            <Wrench className="w-3 h-3 inline" />
          </button>
        </div>
      </td>
      <td className="px-2 py-2 min-w-[120px]">
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
          placeholder="Model…"
          value={item.modelNumber || ''}
          onChange={e => onChange(item.id, { modelNumber: e.target.value })}
        />
      </td>
      <td className="px-2 py-2 min-w-[80px]">
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
          placeholder="2 Yr…"
          value={item.warrantyPeriod || ''}
          onChange={e => onChange(item.id, { warrantyPeriod: e.target.value })}
        />
      </td>
      <td className="px-2 py-2 w-16">
        <input
          type="number"
          min={1}
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none text-center"
          value={item.quantity}
          onChange={e => onChange(item.id, { quantity: Math.max(1, Number(e.target.value)) })}
        />
      </td>
      <td className="px-2 py-2 w-20">
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none"
          value={item.unit || 'Pcs'}
          onChange={e => onChange(item.id, { unit: e.target.value })}
        />
      </td>
      <td className="px-2 py-2 w-28">
        <input
          type="number"
          min={0}
          step={0.01}
          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none text-right"
          value={item.unitPrice}
          onChange={e => onChange(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
        />
      </td>
      <td className="px-2 py-2 w-28">
        <div className="flex gap-1">
          <input
            type="number"
            min={0}
            step={0.01}
            className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none text-right"
            value={item.discountValue || 0}
            onChange={e => onChange(item.id, { discountValue: parseFloat(e.target.value) || 0 })}
          />
          <select
            className="w-12 text-xs border border-gray-200 rounded-lg px-1 py-1.5 focus:outline-none bg-white"
            value={item.discountType || 'fixed'}
            onChange={e => onChange(item.id, { discountType: e.target.value as 'fixed' | 'percentage' })}
          >
            <option value="fixed">Rs.</option>
            <option value="percentage">%</option>
          </select>
        </div>
      </td>
      <td className="px-2 py-2 w-28 text-right font-medium text-gray-900 text-sm">
        {fmtCurrency(item.lineTotal)}
      </td>
      <td className="px-2 py-2 w-8">
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

interface QuotationFormState {
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerAddress: string;
  lineItems: LineItem[];
  overallDiscount: number;
  overallDiscountType: 'percentage' | 'fixed';
  installationCost: number;
  labourCost: number;
  additionalCharges: number;
  taxRate: number;
  validityDate: string;
  paymentTerms: string;
  notes: string;
  termsAndConditions: string;
  productWarranty: string;
  serviceWarranty: string;
}

const EMPTY_FORM: QuotationFormState = {
  customerId: '', customerName: '', customerPhone: '', customerEmail: '', customerAddress: '',
  lineItems: [],
  overallDiscount: 0, overallDiscountType: 'fixed',
  installationCost: 0, labourCost: 0, additionalCharges: 0, taxRate: 0,
  validityDate: '', paymentTerms: 'Net 30', notes: '',
  termsAndConditions: 'All prices are in Sri Lankan Rupees (LKR). Prices are valid for the validity period stated above.',
  productWarranty: '1 Year Product Warranty (Manufacturer)',
  serviceWarranty: '1 Year K-Tec Service Warranty',
};

export default function QuotationsPage() {
  const navigate = useNavigate();
  const { quotations, addQuotation, updateQuotation, deleteQuotation, approveQuotation, rejectQuotation, convertQuotationToInvoice } = useWorkflowStore();
  const { catalogProducts: _catalogProducts, packages } = useCatalogStore();
  const { customers, products: adminProducts } = useAdminStore();
  const inventory = adminProducts; // adminStore.products doubles as inventory

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'all'>('all');
  const [showDetail, setShowDetail] = useState<WorkflowQuotation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<QuotationFormState>(EMPTY_FORM);

  // Product Picker Modal state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetLineId, setPickerTargetLineId] = useState<string | null>(null);

  const handleOpenPicker = (lineItemId: string) => {
    setPickerTargetLineId(lineItemId);
    setPickerOpen(true);
  };

  const handlePickerSelectProduct = (product: {
    name: string; modelNumber: string; warrantyPeriod: string;
    unitPrice: number; description: string; stock: number;
    brandName: string; categoryName: string;
  }) => {
    if (!pickerTargetLineId) return;
    updateLine(pickerTargetLineId, {
      itemType: 'product',
      name: product.name,
      modelNumber: product.modelNumber,
      warrantyPeriod: product.warrantyPeriod,
      unitPrice: product.unitPrice,
      description: product.description,
      quantity: 1,
      unit: 'Pcs',
      discountValue: 0,
      discountType: 'fixed',
    });
    setPickerOpen(false);
    setPickerTargetLineId(null);
  };

  const handlePickerSelectPackageSingle = (pkg: any) => {
    if (!pickerTargetLineId) return;
    updateLine(pickerTargetLineId, {
      itemType: 'package',
      packageId: pkg.id,
      name: pkg.title,
      description: pkg.description || '',
      unitPrice: pkg.sellingPrice || 0,
      quantity: 1,
      unit: 'Package',
      discountValue: 0,
      discountType: 'fixed',
    });
    setPickerOpen(false);
    setPickerTargetLineId(null);
  };

  const handlePickerSelectPackageExpanded = (pkg: any, items: any[]) => {
    if (!pickerTargetLineId) return;
    // Replace target line with first item, then add remaining as new lines
    const lineItems = items.map((item, idx) => ({
      ...EMPTY_LINE,
      id: idx === 0 ? pickerTargetLineId : uid(),
      itemType: 'product' as const,
      name: item.name || 'Package Item',
      modelNumber: item.modelNumber || '',
      warrantyPeriod: item.warrantyPeriod || '',
      unitPrice: item.unitPrice || 0,
      description: item.description || `From: ${pkg.title}`,
      quantity: 1,
      unit: 'Pcs',
      discountValue: 0,
      discountType: 'fixed' as const,
      lineTotal: item.unitPrice || 0,
    }));
    setForm(f => {
      // Replace the target line with all expanded lines
      const existing = f.lineItems.filter(l => l.id !== pickerTargetLineId);
      return { ...f, lineItems: [...existing, ...lineItems] };
    });
    setPickerOpen(false);
    setPickerTargetLineId(null);
  };

  const filtered = useMemo(() => quotations.filter(q => {
    const matchSearch = q.customerName.toLowerCase().includes(search.toLowerCase()) ||
      q.quotationNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchSearch && matchStatus;
  }), [quotations, search, statusFilter]);

  // ---------- Line item helpers ----------
  const updateLine = (id: string, patch: Partial<LineItem>) => {
    setForm(f => ({
      ...f,
      lineItems: f.lineItems.map(l => {
        if (l.id !== id) return l;
        const updated = { ...l, ...patch };
        return { ...updated, lineTotal: calcLine(updated) };
      }),
    }));
  };
  const addLine = () => {
    const newLine: LineItem = { ...EMPTY_LINE, id: uid(), lineTotal: 0 };
    setForm(f => ({ ...f, lineItems: [...f.lineItems, newLine] }));
  };
  const deleteLine = (id: string) => {
    setForm(f => ({ ...f, lineItems: f.lineItems.filter(l => l.id !== id) }));
  };

  // ---------- Totals ----------
  const totals = useMemo(() => {
    const subtotal = form.lineItems.reduce((s, l) => s + l.lineTotal, 0);
    const itemDiscount = form.lineItems.reduce((s, l) => s + (l.quantity * l.unitPrice - l.lineTotal), 0);
    const overallDiscAmt = form.overallDiscountType === 'percentage'
      ? subtotal * (form.overallDiscount / 100) : form.overallDiscount;
    const beforeTax = subtotal - overallDiscAmt + form.installationCost + form.labourCost + form.additionalCharges;
    const taxAmount = beforeTax * (form.taxRate / 100);
    const grandTotal = beforeTax + taxAmount;
    return { subtotal, itemDiscount, overallDiscAmt, taxAmount, grandTotal };
  }, [form]);

  // ---------- Customer picker ----------
  const handlePickCustomer = (cid: string) => {
    const c = customers.find(c => c.id === cid);
    if (!c) return;
    setForm(f => ({
      ...f, customerId: c.id, customerName: c.name,
      customerPhone: c.phone || '', customerEmail: c.email || '',
      customerAddress: c.address || '',
    }));
  };

  // ---------- Save ----------
  const handleSave = (asDraft = true) => {
    if (!form.customerName.trim() || !form.customerPhone.trim()) {
      toast.error('Required', 'Customer name and phone are required.');
      return;
    }
    const data = {
      ...form,
      subtotal: totals.subtotal,
      itemDiscount: totals.itemDiscount,
      taxAmount: totals.taxAmount,
      grandTotal: totals.grandTotal,
      status: (asDraft ? 'draft' : 'sent') as QuotationStatus,
    };
    if (editingId) {
      updateQuotation(editingId, data);
      toast.success('Saved', 'Quotation updated.');
    } else {
      addQuotation(data);
      toast.success('Created', 'Quotation created.');
    }
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  // ---------- Edit ----------
  const handleEdit = (q: WorkflowQuotation) => {
    setForm({
      customerId: q.customerId || '',
      customerName: q.customerName,
      customerPhone: q.customerPhone,
      customerEmail: q.customerEmail || '',
      customerAddress: q.customerAddress || '',
      lineItems: q.lineItems,
      overallDiscount: q.overallDiscount,
      overallDiscountType: q.overallDiscountType,
      installationCost: q.installationCost,
      labourCost: q.labourCost,
      additionalCharges: q.additionalCharges,
      taxRate: q.taxRate,
      validityDate: q.validityDate || '',
      paymentTerms: q.paymentTerms || '',
      notes: q.notes || '',
      termsAndConditions: q.termsAndConditions || '',
      productWarranty: q.productWarranty || '',
      serviceWarranty: q.serviceWarranty || '',
    });
    setEditingId(q.id);
    setShowForm(true);
    setShowDetail(null);
  };

  // ---------- Convert ----------
  const handleConvert = (qId: string) => {
    const invId = convertQuotationToInvoice(qId);
    if (invId) {
      toast.success('Invoice Created', 'Quotation converted to invoice.');
      setTimeout(() => navigate('/admin/invoices'), 1200);
    } else {
      toast.error('Cannot Convert', 'Only approved quotations can be converted.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Quotations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Professional quotation builder</p>
        </div>
        <button
          onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#7FA86A] text-white rounded-xl text-sm font-medium hover:bg-[#658C53] transition-colors"
        >
          <Plus className="w-4 h-4" /> New Quotation
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
            placeholder="Search quotations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {(['all','draft','sent','approved','rejected','converted','expired'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s as any)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors capitalize ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{s === 'all' ? 'All' : s}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-gray-400">
            <FileText className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">No quotations yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Number</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(q => (
                  <tr key={q.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{q.quotationNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{q.customerName}</div>
                      <div className="text-xs text-gray-500">{q.customerPhone}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{q.lineItems.length} item(s)</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{fmtCurrency(q.grandTotal)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(q.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[q.status]}`}>
                        {q.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setShowDetail(q)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleEdit(q)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#7FA86A] hover:bg-[#F1F6EE]">
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        {q.status !== 'approved' && q.status !== 'converted' && (
                          <button onClick={() => { approveQuotation(q.id); toast.success('Approved', 'Quotation approved.'); }} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {q.status === 'approved' && (
                          <button onClick={() => handleConvert(q.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50">
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => { deleteQuotation(q.id); if (showDetail?.id === q.id) setShowDetail(null); }} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===================== QUOTATION BUILDER ===================== */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowForm(false)} />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white z-50 overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="font-semibold text-gray-900 text-lg">
                    {editingId ? 'Edit Quotation' : 'New Quotation'}
                  </h2>
                  <p className="text-xs text-gray-500">Professional quotation builder</p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-8">
                {/* CUSTOMER */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#7FA86A] text-white rounded-full text-xs flex items-center justify-center">1</span>
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Select Existing Customer</label>
                      <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
                        value={form.customerId}
                        onChange={e => handlePickCustomer(e.target.value)}
                      >
                        <option value="">— Select customer or enter manually below —</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
                      </select>
                    </div>
                    {[
                      { label: 'Customer Name *', key: 'customerName', required: true },
                      { label: 'Phone Number *', key: 'customerPhone', required: true },
                      { label: 'Email Address', key: 'customerEmail', required: false },
                      { label: 'Address', key: 'customerAddress', required: false },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">{f.label}</label>
                        <input
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
                          value={(form as any)[f.key]}
                          onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                          required={f.required}
                        />
                      </div>
                    ))}
                  </div>
                </section>

                {/* LINE ITEMS */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#7FA86A] text-white rounded-full text-xs flex items-center justify-center">2</span>
                    Line Items
                  </h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-2 py-2 w-6"></th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Product / Service</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Model</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Warranty</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Qty</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Unit</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Unit Price</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">Discount</th>
                          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500">Total</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.lineItems.map(item => (
                          <LineItemRow
                            key={item.id}
                            item={item}
                            onChange={updateLine}
                            onDelete={deleteLine}
                            onOpenPicker={handleOpenPicker}
                          />
                        ))}
                      </tbody>
                    </table>
                    {form.lineItems.length === 0 && (
                      <div className="py-8 text-center text-gray-400 text-sm">No items added yet. Click "+ Add Item" below.</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={addLine}
                    className="mt-3 flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#7FA86A] hover:text-[#7FA86A] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Item
                  </button>
                </section>

                {/* CALCULATIONS */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#7FA86A] text-white rounded-full text-xs flex items-center justify-center">3</span>
                    Calculations
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      {[
                        { label: 'Installation Cost (Rs.)', key: 'installationCost' },
                        { label: 'Labour Cost (Rs.)', key: 'labourCost' },
                        { label: 'Additional Charges (Rs.)', key: 'additionalCharges' },
                        { label: 'Tax Rate (%)', key: 'taxRate' },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">{f.label}</label>
                          <input
                            type="number" min={0} step={0.01}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
                            value={(form as any)[f.key]}
                            onChange={e => setForm(p => ({ ...p, [f.key]: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                      ))}
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Overall Discount</label>
                        <div className="flex gap-2">
                          <input
                            type="number" min={0} step={0.01}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
                            value={form.overallDiscount}
                            onChange={e => setForm(p => ({ ...p, overallDiscount: parseFloat(e.target.value) || 0 }))}
                          />
                          <select
                            className="w-20 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none bg-white"
                            value={form.overallDiscountType}
                            onChange={e => setForm(p => ({ ...p, overallDiscountType: e.target.value as 'fixed'|'percentage' }))}
                          >
                            <option value="fixed">Rs.</option>
                            <option value="percentage">%</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 self-start">
                      <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{fmtCurrency(totals.subtotal)}</span></div>
                      {totals.itemDiscount > 0 && <div className="flex justify-between text-sm text-red-600"><span>Item Discounts</span><span>- {fmtCurrency(totals.itemDiscount)}</span></div>}
                      {form.overallDiscount > 0 && <div className="flex justify-between text-sm text-red-600"><span>Overall Discount</span><span>- {fmtCurrency(totals.overallDiscAmt)}</span></div>}
                      {form.installationCost > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Installation</span><span>{fmtCurrency(form.installationCost)}</span></div>}
                      {form.labourCost > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Labour</span><span>{fmtCurrency(form.labourCost)}</span></div>}
                      {form.additionalCharges > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Additional</span><span>{fmtCurrency(form.additionalCharges)}</span></div>}
                      {form.taxRate > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Tax ({form.taxRate}%)</span><span>{fmtCurrency(totals.taxAmount)}</span></div>}
                      <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-base">
                        <span>Grand Total</span><span className="text-[#7FA86A]">{fmtCurrency(totals.grandTotal)}</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* TERMS */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#7FA86A] text-white rounded-full text-xs flex items-center justify-center">4</span>
                    Terms & Conditions
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Quotation Validity Date</label>
                      <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" value={form.validityDate} onChange={e => setForm(p => ({ ...p, validityDate: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Payment Terms</label>
                      <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" value={form.paymentTerms} onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Product Warranty</label>
                      <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" value={form.productWarranty} onChange={e => setForm(p => ({ ...p, productWarranty: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">K-Tec Service Warranty</label>
                      <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" value={form.serviceWarranty} onChange={e => setForm(p => ({ ...p, serviceWarranty: e.target.value }))} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Notes</label>
                      <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Terms and Conditions</label>
                      <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none" value={form.termsAndConditions} onChange={e => setForm(p => ({ ...p, termsAndConditions: e.target.value }))} />
                    </div>
                  </div>
                </section>

                {/* ACTION BUTTONS */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                  <button onClick={() => handleSave(true)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
                    Save Draft
                  </button>
                  <button onClick={() => handleSave(false)} className="px-5 py-2.5 bg-[#7FA86A] text-white rounded-xl text-sm font-medium hover:bg-[#658C53] transition-colors flex items-center gap-2">
                    <Send className="w-4 h-4" /> Save & Send
                  </button>
                  <button onClick={() => setShowForm(false)} className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors ml-auto">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* QUOTATION DETAIL DRAWER */}
      <AnimatePresence>
        {showDetail && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowDetail(null)} />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">Quotation Details</h2>
                  <p className="text-xs font-mono text-gray-500">{showDetail.quotationNumber}</p>
                </div>
                <button onClick={() => setShowDetail(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[showDetail.status]}`}>{showDetail.status}</span>
                  <span className="text-xs text-gray-400">{fmtDate(showDetail.createdAt)}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold">{showDetail.customerName}</p>
                  <p className="text-sm text-gray-600">{showDetail.customerPhone}</p>
                  {showDetail.customerEmail && <p className="text-sm text-gray-500">{showDetail.customerEmail}</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Line Items</p>
                  {showDetail.lineItems.map((li, i) => (
                    <div key={li.id} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                      <div>
                        <span className="font-medium">{li.name}</span>
                        {li.modelNumber && <span className="text-gray-400 ml-2 text-xs">({li.modelNumber})</span>}
                        <div className="text-xs text-gray-400">{li.quantity} × {fmtCurrency(li.unitPrice)}</div>
                      </div>
                      <span className="font-medium">{fmtCurrency(li.lineTotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmtCurrency(showDetail.subtotal)}</span></div>
                  {showDetail.installationCost > 0 && <div className="flex justify-between"><span className="text-gray-500">Installation</span><span>{fmtCurrency(showDetail.installationCost)}</span></div>}
                  {showDetail.taxRate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({showDetail.taxRate}%)</span><span>{fmtCurrency(showDetail.taxAmount)}</span></div>}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-100">
                    <span>Grand Total</span><span className="text-[#7FA86A]">{fmtCurrency(showDetail.grandTotal)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => handleEdit(showDetail)} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">
                    <FileText className="w-4 h-4" /> Edit
                  </button>
                  {showDetail.status !== 'approved' && showDetail.status !== 'converted' && (
                    <button onClick={() => { approveQuotation(showDetail.id); setShowDetail({ ...showDetail, status: 'approved' }); toast.success('Approved',''); }} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-sm hover:bg-emerald-100">
                      <Check className="w-4 h-4" /> Approve
                    </button>
                  )}
                  {showDetail.status === 'approved' && (
                    <button onClick={() => { handleConvert(showDetail.id); setShowDetail(null); }} className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-xl text-sm hover:bg-teal-100">
                      <ArrowRight className="w-4 h-4" /> Convert to Invoice
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* PRODUCT PICKER MODAL */}
      <ProductPickerModal
        isOpen={pickerOpen}
        onClose={() => { setPickerOpen(false); setPickerTargetLineId(null); }}
        onSelectProduct={handlePickerSelectProduct}
        onSelectPackageSingle={handlePickerSelectPackageSingle}
        onSelectPackageExpanded={handlePickerSelectPackageExpanded}
      />
    </div>
  );
}
